import { useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { useLanguage } from '../../../hooks';
import { convertHoursToDaysAndHours } from '../../NewWebForms/Questions/dateQuestion/dateValidationHelpers';
import { getOffsetUnit, takesNoOffset, takesTwoOffsets } from './comparisons';
import { getOffsetBounds } from './constants';
import {
  DateQuestionField,
  DateQuestionModeType,
  DateValidationType,
  RangeConditionType,
} from './types';
import {
  convertDisplayOffsetValueToOffsetHours,
  convertOffsetHoursToDisplayOffsetValue,
  isUnsatisfiableNotBetweenTimeWindow,
} from './utils';

const customStyles: Record<string, SxProps<Theme>> = {
  rangeInputsRow: {
    display: 'flex',
    gap: '32px',
    alignItems: 'center',
  },
  simpleOffsetInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  rangeValueColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  rangeValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  andColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimumOrMaximumValueTextField: {
    width: '70px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
  simpleOffsetTextField: {
    width: '80px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
  offsetLabel: {
    fontSize: '14px',
    mb: '8px',
  },
  andLabel: {
    fontSize: '14px',
  },
  offsetUnitTimeLabel: {
    fontSize: '14px',
  },
  validationErrorMessage: {
    color: 'error.main',
    fontSize: '12px',
    mt: '8px',
  },
};

interface CustomRangeConditionFieldsProps {
  /** Which mode's bounds apply: the relative mode drops the 24-hour floor and caps the hours field (AC-13, AC-18). */
  dateValidationType?: DateValidationType | '';
  selectedRangeCondition: RangeConditionType;
  dateMode: DateQuestionModeType;
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  questionId: string;
  tableId?: string;
  onDateValidationChange: (
    questionId: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
    tableId?: string,
  ) => void;
}

export const CustomRangeConditionFields = (props: CustomRangeConditionFieldsProps) => {
  const {
    selectedRangeCondition,
    dateMode,
    dateValidationType,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    questionId,
    tableId,
    onDateValidationChange,
  } = props;

  const lang = useLanguage();
  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';

  // Relative datetime splits the single offset into Días (unbounded) + Horas (0-23) fields
  // that combine into the one stored `offsetHours` (PRD: "días y horas en fecha y hora").
  // Hooks run unconditionally — the early returns below (predefined/two-sided conditions)
  // are prop-driven and can flip between renders, so this state must exist regardless of
  // which branch this render takes.
  const isRelativeDatetimeSplit =
    dateValidationType === 'relativeRange' &&
    safeDateMode === 'datetime' &&
    !!selectedRangeCondition &&
    !takesNoOffset(selectedRangeCondition) &&
    !takesTwoOffsets(selectedRangeCondition);
  const [daysInput, setDaysInput] = useState('');
  const [hoursInput, setHoursInput] = useState('');
  // Guards the very first sync below: before it, the local fields' pristine '' default would
  // otherwise coincidentally "match" an incoming offsetHours of '0' (both read as total 0),
  // skipping the decompose and leaving a freshly-selected condition's Días/Horas blank instead
  // of showing '0'/'0' -- indistinguishable, without this, from Días/Horas genuinely having
  // been cleared to that same total by the user.
  const hasSyncedFromPropsRef = useRef(false);

  /**
   * Syncs the two display fields from the stored `offsetHours`. After the first sync, skips
   * re-deriving them when the incoming total already matches what's locally displayed -- that's
   * this component's own last dispatch echoing back through props, not an external change (a
   * question switch, someone else's edit) -- so e.g. clearing Días to retype doesn't get
   * immediately overwritten by the previous total re-decomposing back onto it.
   */
  useEffect(() => {
    if (!isRelativeDatetimeSplit) return;
    if (hasSyncedFromPropsRef.current) {
      const currentLocalTotal =
        (daysInput === '' ? 0 : Number(daysInput)) * 24 +
        (hoursInput === '' ? 0 : Number(hoursInput));
      const incomingTotal =
        offsetHours === undefined || offsetHours === '' ? 0 : Number(offsetHours);
      if (!isNaN(incomingTotal) && incomingTotal === currentLocalTotal) return;
    }
    hasSyncedFromPropsRef.current = true;
    if (offsetHours === undefined || offsetHours === '' || isNaN(Number(offsetHours))) {
      setDaysInput('');
      setHoursInput('');
      return;
    }
    const { days, hours } = convertHoursToDaysAndHours(offsetHours);
    setDaysInput(String(days));
    setHoursInput(String(hours));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offsetHours, isRelativeDatetimeSplit]);

  if (!selectedRangeCondition) return <></>;

  const isPredefinedRangeCondition = takesNoOffset(selectedRangeCondition);
  if (isPredefinedRangeCondition) return <></>;

  const getUnitTimeText = (value: number) => {
    const isSingular = value === 1;
    return getOffsetUnit(safeDateMode) === 'days'
      ? isSingular
        ? lang.form_builder.dateValidation.day
        : lang.form_builder.dateValidation.days
      : isSingular
      ? lang.form_builder.dateValidation.hour
      : lang.form_builder.dateValidation.hours;
  };

  const handleOffsetChange =
    (dateQuestionFieldToUpdate: DateQuestionField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newOffsetValue = event.target.value;

      // This allows to delete the value of the field by pressing the delete key
      if (newOffsetValue === '') {
        onDateValidationChange(questionId, dateQuestionFieldToUpdate, '', tableId);
        return;
      }

      const isNonNegativeInteger = /^\d+$/.test(newOffsetValue);
      if (!isNonNegativeInteger) {
        return;
      }

      onDateValidationChange(
        questionId,
        dateQuestionFieldToUpdate,
        convertDisplayOffsetValueToOffsetHours(newOffsetValue, safeDateMode),
        tableId,
      );
    };

  const isBetweenTypeRangeCondition = takesTwoOffsets(selectedRangeCondition);
  const { min: minOffsetAllowedHours, max: maxOffsetAllowedHours } = getOffsetBounds(
    safeDateMode,
    dateValidationType,
  );
  const minOffsetAllowedValue = Number(
    convertOffsetHoursToDisplayOffsetValue(minOffsetAllowedHours.toString(), safeDateMode),
  );
  const maxOffsetAllowedValue =
    maxOffsetAllowedHours !== null
      ? Number(
          convertOffsetHoursToDisplayOffsetValue(maxOffsetAllowedHours.toString(), safeDateMode),
        )
      : null;

  if (isBetweenTypeRangeCondition) {
    const offsetToLeftValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToLeft || '', safeDateMode),
    );
    const offsetToRightValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToRight || '', safeDateMode),
    );

    const isInvalidOffsetToLeftValue =
      isNaN(offsetToLeftValue) ||
      offsetToLeftValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetToLeftValue > maxOffsetAllowedValue);

    const isInvalidOffsetToRightValue =
      isNaN(offsetToRightValue) ||
      offsetToRightValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetToRightValue > maxOffsetAllowedValue);

    const hasUnsatisfiableTimeWindow = isUnsatisfiableNotBetweenTimeWindow(
      selectedRangeCondition,
      safeDateMode,
      isInvalidOffsetToLeftValue,
      isInvalidOffsetToRightValue,
      offsetToLeftValue,
      offsetToRightValue,
    );
    const hasAnyOffsetValidationError =
      isInvalidOffsetToLeftValue || isInvalidOffsetToRightValue || hasUnsatisfiableTimeWindow;

    const rangeValidationErrorMessage = hasUnsatisfiableTimeWindow
      ? lang.form_builder.dateValidation.notBetweenTimeWindowTooWideMessage
      : hasAnyOffsetValidationError
      ? safeDateMode === 'date'
        ? lang.form_builder.dateValidation.rangeValidationErrorMessageForDate
        : safeDateMode === 'time'
        ? lang.form_builder.dateValidation.rangeValidationErrorMessageForTime
        : lang.form_builder.dateValidation.rangeValidationErrorMessageForDateTime
      : '';

    const leftUnitText = getUnitTimeText(offsetToLeftValue);
    const leftUnitTextCapitalized = leftUnitText.charAt(0).toUpperCase() + leftUnitText.slice(1);
    const rightUnitText = getUnitTimeText(offsetToRightValue);
    const rightUnitTextCapitalized = rightUnitText.charAt(0).toUpperCase() + rightUnitText.slice(1);

    return (
      <>
        <Box sx={customStyles.rangeInputsRow}>
          <Box sx={customStyles.rangeValueColumn}>
            <Typography sx={customStyles.offsetLabel}>
              {lang.form_builder.dateValidation.minimumValue}
            </Typography>
            <Box sx={customStyles.rangeValueRow}>
              <TextField
                label=''
                value={convertOffsetHoursToDisplayOffsetValue(
                  offsetHoursToLeft || '',
                  safeDateMode,
                )}
                size='small'
                type='text'
                sx={customStyles.minimumOrMaximumValueTextField}
                onChange={handleOffsetChange('offsetHoursToLeft')}
                error={isInvalidOffsetToLeftValue || hasUnsatisfiableTimeWindow}
                inputProps={{
                  inputMode: 'numeric',
                }}
              />
              <Typography sx={customStyles.offsetUnitTimeLabel}>
                {leftUnitTextCapitalized}
              </Typography>
            </Box>
          </Box>

          <Box sx={customStyles.andColumn}>
            <Typography sx={customStyles.andLabel}>
              {lang.form_builder.dateValidation.and}
            </Typography>
          </Box>

          <Box sx={customStyles.rangeValueColumn}>
            <Typography sx={customStyles.offsetLabel}>
              {lang.form_builder.dateValidation.maximumValue}
            </Typography>
            <Box sx={customStyles.rangeValueRow}>
              <TextField
                label=''
                value={convertOffsetHoursToDisplayOffsetValue(
                  offsetHoursToRight || '',
                  safeDateMode,
                )}
                size='small'
                type='text'
                sx={customStyles.minimumOrMaximumValueTextField}
                onChange={handleOffsetChange('offsetHoursToRight')}
                error={isInvalidOffsetToRightValue || hasUnsatisfiableTimeWindow}
                inputProps={{
                  inputMode: 'numeric',
                }}
              />
              <Typography sx={customStyles.offsetUnitTimeLabel}>
                {rightUnitTextCapitalized}
              </Typography>
            </Box>
          </Box>
        </Box>
        {rangeValidationErrorMessage && (
          <Typography sx={customStyles.validationErrorMessage}>
            {rangeValidationErrorMessage}
          </Typography>
        )}
      </>
    );
  }

  if (isRelativeDatetimeSplit) {
    const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw !== '' && !/^\d+$/.test(raw)) return;
      setDaysInput(raw);
      const daysNum = raw === '' ? 0 : Number(raw);
      // Horas is always already normalized to 0-23 by handleHoursChange below, so it never
      // needs reading back for validity here.
      const hoursNum = hoursInput === '' ? 0 : Number(hoursInput);
      onDateValidationChange(questionId, 'offsetHours', String(daysNum * 24 + hoursNum), tableId);
    };

    const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw !== '' && !/^\d+$/.test(raw)) return;
      const typedHours = raw === '' ? 0 : Number(raw);
      const daysNum = daysInput === '' ? 0 : Number(daysInput);
      // Normalize on entry rather than reject: Días and Horas combine into one stored total,
      // which cannot tell "30 entered directly into Horas" apart from "1 day 6 hours entered
      // correctly" -- so rejecting an over-cap value without dispatching left the panel
      // displaying an offset it hadn't actually saved (review round 2). Carrying the overflow
      // into Días instead keeps every dispatch in sync with what's displayed, satisfies the
      // cap as a rendering invariant (Horas never shows above 23), and needs no error state.
      const carryDays = Math.floor(typedHours / 24);
      const normalizedHours = typedHours - carryDays * 24;
      const newDays = daysNum + carryDays;
      // Only write the field that actually changed. Always overwriting Horas refilled a
      // cleared field with '0' the instant it emptied (Días doesn't have this problem --
      // handleDaysChange passes `raw` through as-is), and always overwriting Días stamped a
      // '0' into it on every Horas keystroke, making a fresh "5 hours after" look pre-filled
      // with "Días: 0" when nothing was entered there. An empty Horas still dispatches 0 below,
      // so the stored total and the display/storage agreement are unaffected either way
      // (review round 3).
      setHoursInput(raw === '' ? '' : String(normalizedHours));
      if (carryDays > 0) setDaysInput(String(newDays));
      onDateValidationChange(
        questionId,
        'offsetHours',
        String(newDays * 24 + normalizedHours),
        tableId,
      );
    };

    const daysLabel = lang.form_builder.dateValidation.days;
    const daysLabelCapitalized = daysLabel.charAt(0).toUpperCase() + daysLabel.slice(1);
    const hoursLabel = lang.form_builder.dateValidation.hours;
    const hoursLabelCapitalized = hoursLabel.charAt(0).toUpperCase() + hoursLabel.slice(1);

    return (
      <Box sx={customStyles.rangeInputsRow}>
        <Box sx={customStyles.rangeValueColumn}>
          <Typography sx={customStyles.offsetLabel}>{daysLabelCapitalized}</Typography>
          <TextField
            label=''
            value={daysInput}
            size='small'
            type='text'
            sx={customStyles.minimumOrMaximumValueTextField}
            onChange={handleDaysChange}
            inputProps={{
              inputMode: 'numeric',
            }}
          />
        </Box>
        <Box sx={customStyles.rangeValueColumn}>
          <Typography sx={customStyles.offsetLabel}>{hoursLabelCapitalized}</Typography>
          <TextField
            label=''
            value={hoursInput}
            size='small'
            type='text'
            sx={customStyles.minimumOrMaximumValueTextField}
            onChange={handleHoursChange}
            inputProps={{
              inputMode: 'numeric',
            }}
          />
        </Box>
      </Box>
    );
  }

  const offsetValue = Number(
    convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode),
  );

  const isInvalidOffsetValue =
    isNaN(offsetValue) ||
    offsetValue < minOffsetAllowedValue ||
    (maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue);

  const validationErrorMessageInOffsetField =
    offsetValue < minOffsetAllowedValue
      ? `${
          lang.form_builder.dateValidation.minimumValueForOffsetIs
        } ${minOffsetAllowedValue} ${getUnitTimeText(minOffsetAllowedValue).toLowerCase()}`
      : maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue
      ? `${
          lang.form_builder.dateValidation.maximumValueForOffsetIs
        } ${maxOffsetAllowedValue} ${getUnitTimeText(maxOffsetAllowedValue).toLowerCase()}`
      : '';

  const unitText = getUnitTimeText(offsetValue);
  const unitTextCapitalized = unitText.charAt(0).toUpperCase() + unitText.slice(1);

  return (
    <>
      <Typography sx={customStyles.offsetLabel}>
        {lang.form_builder.dateValidation.offset}
      </Typography>
      <Box sx={customStyles.simpleOffsetInputRow}>
        <TextField
          label=''
          value={convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode)}
          size='small'
          type='text'
          sx={customStyles.simpleOffsetTextField}
          onChange={handleOffsetChange('offsetHours')}
          error={isInvalidOffsetValue}
          inputProps={{
            inputMode: 'numeric',
          }}
        />
        <Typography sx={customStyles.offsetUnitTimeLabel}>{unitTextCapitalized}</Typography>
      </Box>
      {validationErrorMessageInOffsetField && (
        <Typography sx={customStyles.validationErrorMessage}>
          {validationErrorMessageInOffsetField}
        </Typography>
      )}
    </>
  );
};
